/**
 * Trailblazers galaxy-map node markers.
 *
 * Custom SVG markers an admin can assign to a planet node in place of the
 * default white dot. Each one is a finished illustration carrying its own
 * gradients, strokes and inner detail, so it is painted as a real
 * `background-image` (see `.trl-map-builder__dot-art`) and rendered exactly as
 * authored — never recoloured.
 *
 * Every marker also gets shape-accurate scan ripples behind it. They work for
 * ANY marker with no per-shape tuning because they mask the SVG's alpha rather
 * than reading its colours, so the expanding rings trace whatever contour the
 * artwork has (see `.trl-map-builder__dot-ripples`). The admin's per-node colour
 * tints those rings — it is the marker's glow colour, not its fill.
 *
 * The SVGs are served art (see ./art.ts), listed for pickers, and resolved back
 * from a stored id via {@link getMapIconUrl}.
 *
 * To add a marker: drop the SVG in `served/trl/markers/`, run
 * `npm run publish:art` (build:packages does), and add one
 * {@link TRL_MAP_ICONS} entry. Nothing else in the app needs to change — no
 * schema, no upload path, no styling.
 */
import { artUrl } from './art';
function icon(id, label, asset) {
    return { id, label, get url() { return artUrl(asset); } };
}
/**
 * The pickable node markers, in display order (their authoring order on disk).
 * `id` is stored on the node (`TrlMapNodeDef.icon`); a node with no icon — or
 * with an id no longer in this registry — renders the default white dot.
 *
 * Ids come from each file's internal `<g id="icon-…">` name rather than its
 * numeric filename, so a stored id stays meaningful and survives the artwork
 * being renumbered. Three files carry no glyph id and are named from their
 * geometry; two glyph names appear twice on disk and the second of each takes an
 * `-alt` suffix, because an id collision would silently shadow one marker in the
 * lookup below. Once a marker has shipped its id is load-bearing (it lives in
 * `trl_map_nodes.icon` rows) and must not be renamed.
 */
export const TRL_MAP_ICONS = [
    icon('globe', 'Globe', 'trl/markers/1.svg'),
    icon('credit-token', 'Credit Token', 'trl/markers/2.svg'),
    icon('compass-star', 'Compass Star', 'trl/markers/3.svg'),
    icon('route-glyph', 'Route Glyph', 'trl/markers/4.svg'),
    icon('wave-portal', 'Wave Portal', 'trl/markers/5.svg'),
    icon('radar-rings', 'Radar Rings', 'trl/markers/6.svg'),
    icon('warning-badge', 'Warning Badge', 'trl/markers/7.svg'),
    icon('radar-rings-alt', 'Radar Rings (Alt)', 'trl/markers/8.svg'),
    icon('rounded-target', 'Rounded Target', 'trl/markers/9.svg'),
    icon('triangle-orbit', 'Triangle Orbit', 'trl/markers/10.svg'),
    icon('folded-glyph', 'Folded Glyph', 'trl/markers/11.svg'),
    icon('pentagon-target', 'Pentagon Target', 'trl/markers/12.svg'),
    icon('heart-marker', 'Heart Marker', 'trl/markers/13.svg'),
    icon('navigation-prism', 'Navigation Prism', 'trl/markers/14.svg'),
    icon('orbital-rings', 'Orbital Rings', 'trl/markers/15.svg'),
    icon('three-arm-hub', 'Three-Arm Hub', 'trl/markers/16.svg'),
    icon('diamond-disc', 'Diamond Disc', 'trl/markers/17.svg'),
    icon('banded-target', 'Banded Target', 'trl/markers/18.svg'),
    icon('wireframe-cube', 'Wireframe Cube', 'trl/markers/19.svg'),
    icon('quadrant-orbit', 'Quadrant Orbit', 'trl/markers/20.svg'),
    icon('six-sector-disc', 'Six-Sector Disc', 'trl/markers/21.svg'),
    icon('layered-disc', 'Layered Disc', 'trl/markers/22.svg'),
    icon('split-square', 'Split Square', 'trl/markers/23.svg'),
    icon('crosshair-disc', 'Crosshair Disc', 'trl/markers/24.svg'),
    icon('horizon-orb', 'Horizon Orb', 'trl/markers/25.svg'),
    icon('star-flare', 'Star Flare', 'trl/markers/26.svg'),
    icon('hex-play', 'Hex Play', 'trl/markers/27.svg'),
    icon('connected-diamond', 'Connected Diamond', 'trl/markers/28.svg'),
    icon('three-sector-prism', 'Three-Sector Prism', 'trl/markers/29.svg'),
    icon('perspective-square', 'Perspective Square', 'trl/markers/30.svg'),
    icon('rounded-crosshair', 'Rounded Crosshair', 'trl/markers/31.svg'),
    icon('striped-planet', 'Striped Planet', 'trl/markers/32.svg'),
    icon('radial-star', 'Radial Star', 'trl/markers/33.svg'),
    icon('broken-orbital-ring', 'Broken Orbital Ring', 'trl/markers/34.svg'),
    icon('flowing-s-disc', 'Flowing S Disc', 'trl/markers/35.svg'),
    icon('starburst-hex', 'Starburst Hex', 'trl/markers/36.svg'),
    icon('concentric-rings', 'Concentric Rings', 'trl/markers/37.svg'),
    icon('helmet-visor', 'Helmet Visor', 'trl/markers/38.svg'),
    icon('triangle-gate', 'Triangle Gate', 'trl/markers/39.svg'),
    icon('compass-burst', 'Compass Burst', 'trl/markers/40.svg'),
    icon('node-globe', 'Node Globe', 'trl/markers/41.svg'),
    icon('hex-core', 'Hex Core', 'trl/markers/42.svg'),
    icon('gem-location', 'Gem Location', 'trl/markers/43.svg'),
    icon('hex-chamber', 'Hex Chamber', 'trl/markers/44.svg'),
    icon('faceted-cube', 'Faceted Cube', 'trl/markers/45.svg'),
    icon('double-chevron', 'Double Chevron', 'trl/markers/46.svg'),
    icon('crossed-shards', 'Crossed Shards', 'trl/markers/47.svg'),
    icon('cyber-skull', 'Cyber Skull', 'trl/markers/48.svg'),
    icon('segmented-target', 'Segmented Target', 'trl/markers/49.svg'),
    icon('central-cube', 'Central Cube', 'trl/markers/50.svg'),
    icon('nested-squares', 'Nested Squares', 'trl/markers/51.svg'),
    icon('folded-shard', 'Folded Shard', 'trl/markers/52.svg'),
    icon('ring-node', 'Ring Node', 'trl/markers/53.svg'),
    icon('shield-prism', 'Shield Prism', 'trl/markers/54.svg'),
    icon('pyramid-badge', 'Pyramid Badge', 'trl/markers/55.svg'),
    icon('suspended-diamond', 'Suspended Diamond', 'trl/markers/56.svg'),
    icon('ribbon-globe', 'Ribbon Globe', 'trl/markers/57.svg'),
    icon('hex-chamber-alt', 'Hex Chamber (Alt)', 'trl/markers/58.svg'),
    icon('circular-cube', 'Circular Cube', 'trl/markers/59.svg'),
    icon('faceted-mountains', 'Faceted Mountains', 'trl/markers/60.svg'),
    icon('palm-tree', 'Palm Tree', 'trl/markers/61.svg'),
    icon('rounded-frame', 'Rounded Frame', 'trl/markers/62.svg'),
    icon('hex-plate', 'Hex Plate', 'trl/markers/63.svg'),
    icon('triangular-sigil', 'Triangular Sigil', 'trl/markers/64.svg'),
];
const ICON_BY_ID = new Map(TRL_MAP_ICONS.map((i) => [i.id, i]));
/**
 * Resolve a stored icon id to its served SVG URL, or '' when the id is absent
 * or unknown (caller then renders the default white dot).
 */
export function getMapIconUrl(id) {
    if (!id)
        return '';
    return ICON_BY_ID.get(id)?.url ?? '';
}
